import { useState, useEffect, useRef } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useGuest } from "@/contexts/GuestContext";
import { useTourStore } from "@/hooks/useTour";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Sparkles, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import confetti from "canvas-confetti";

// Helper for dynamic despia import
const triggerHaptic = () => {
  import('despia-native').then(({ default: despia }) => {
    despia('haptic://');
  }).catch(() => {});
};

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  route?: string;
  action?: () => void;
}

interface OnboardingStatus {
  hasCompletionAchievement: boolean;
  profile: {
    bio: string | null;
    event_count: number | null;
  } | null;
  hasRSVP: boolean;
  hasJoinedHerd: boolean;
  hasConnectedFriend: boolean;
  hasUsedBuddy: boolean;
}

export const OnboardingChecklist = () => {
  const { user, isAuthStable } = useAuth();
  const { isGuest, promptSignUp } = useGuest();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hasShownCelebration, setHasShownCelebration] = useState(false);
  
  // Local cache for completion status to prevent checklist reappearing during auth transitions
  const [localCompletionStatus, setLocalCompletionStatus] = useState(false);
  
  // Read user-scoped localStorage once user is available
  useEffect(() => {
    if (user?.id) {
      const cached = localStorage.getItem(`onboarding_achievement_completed_${user.id}`) === 'true';
      if (cached) setLocalCompletionStatus(true);
    }
  }, [user?.id]);
  
  // Track if user was previously authenticated to prevent flash during token refresh
  const wasAuthenticated = useRef(false);
  
  useEffect(() => {
    if (user) {
      wasAuthenticated.current = true;
    }
  }, [user]);

  // Static demo checklist for guests
  const demoChecklist: ChecklistItem[] = [
    {
      id: 'profile',
      title: 'Complete Your Profile',
      description: 'Add a bio and photo so others can connect with you (~1 min)',
      completed: false,
      route: '/me',
    },
    {
      id: 'rsvp',
      title: 'RSVP to Your First Event',
      description: "Tap an event → hit 'RSVP' to save your spot",
      completed: false,
      route: '/events',
    },
    {
      id: 'buddy',
      title: 'Try the Buddy System',
      description: "Tip: Toggle 'Find a Buddy' when RSVPing to get matched with someone",
      completed: false,
      route: '/events',
    },
    {
      id: 'herd',
      title: 'Join a Herd',
      description: "Browse communities → tap 'Join' on one that interests you",
      completed: false,
      route: '/herds',
    },
    {
      id: 'friend',
      title: 'Connect with Someone',
      description: "Visit someone's profile → tap 'Add Friend' to connect",
      completed: false,
      route: '/social',
    },
    {
      id: 'attend',
      title: 'Attend Your First Event',
      description: 'Show up and tap "Check In" when you arrive at the venue',
      completed: false,
      route: '/schedule',
    },
  ];

  // OPTIMIZED: Single consolidated query for all onboarding status
  const { data: onboardingStatus, isLoading } = useQuery({
    queryKey: ['onboarding-status', user?.id],
    queryFn: async (): Promise<OnboardingStatus> => {
      if (!user) {
        return {
          hasCompletionAchievement: false,
          profile: null,
          hasRSVP: false,
          hasJoinedHerd: false,
          hasConnectedFriend: false,
          hasUsedBuddy: false,
        };
      }

      // Execute all queries in parallel
      const [
        achievementResult,
        profileResult,
        rsvpResult,
        herdResult,
        friendResult,
        buddyResult,
      ] = await Promise.all([
        supabase
          .from('user_achievements')
          .select('id')
          .eq('user_id', user.id)
          .eq('achievement_type', 'onboarding_complete')
          .limit(1),
        supabase
          .from('profiles')
          .select('bio, event_count')
          .eq('id', user.id)
          .maybeSingle(),

        supabase
          .from('event_attendances')
          .select('id')
          .eq('user_id', user.id)
          .limit(1),
        supabase
          .from('herd_members')
          .select('id')
          .eq('user_id', user.id)
          .limit(1),
        supabase
          .from('user_connections')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'accepted')
          .limit(1),
        // Check buddy usage - either requested a buddy OR accepted someone's buddy request
        supabase
          .from('buddy_requests')
          .select('id')
          .or(`requester_id.eq.${user.id},accepted_by.eq.${user.id}`)
          .limit(1),
      ]);

      // Any failed sub-query previously read as "not done", which is what made
      // a completed checklist silently revert to "0 of 6". Fail loudly instead
      // so React Query retries and the card stays hidden until we know.
      const failed = [
        achievementResult,
        profileResult,
        rsvpResult,
        herdResult,
        friendResult,
        buddyResult,
      ].find((r) => r.error);
      if (failed?.error) throw failed.error;

      return {
        hasCompletionAchievement: (achievementResult.data?.length || 0) > 0,
        profile: profileResult.data,
        hasRSVP: (rsvpResult.data?.length || 0) > 0,
        hasJoinedHerd: (herdResult.data?.length || 0) > 0,
        hasConnectedFriend: (friendResult.data?.length || 0) > 0,
        hasUsedBuddy: (buddyResult.data?.length || 0) > 0,
      };
    },
    enabled: !!user && !isGuest && isAuthStable,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    placeholderData: keepPreviousData, // Keep previous data during brief auth transitions
    retry: 2,
  });


  // Extract values from consolidated query
  const hasCompletionAchievement = onboardingStatus?.hasCompletionAchievement ?? false;
  const profile = onboardingStatus?.profile;
  const hasRSVP = onboardingStatus?.hasRSVP ?? false;
  const hasJoinedHerd = onboardingStatus?.hasJoinedHerd ?? false;
  const hasConnectedFriend = onboardingStatus?.hasConnectedFriend ?? false;
  const hasUsedBuddy = onboardingStatus?.hasUsedBuddy ?? false;
  const hasAttended = (profile?.event_count || 0) > 0;

  // Sync celebration shown state and local completion cache with database achievement
  useEffect(() => {
    if (hasCompletionAchievement) {
      setHasShownCelebration(true);
      setLocalCompletionStatus(true);
      if (user?.id) {
        localStorage.setItem(`onboarding_celebration_shown_${user.id}`, 'true');
        localStorage.setItem(`onboarding_achievement_completed_${user.id}`, 'true');
      }
    }
  }, [hasCompletionAchievement]);

  const userChecklist: ChecklistItem[] = [
    {
      id: 'profile',
      title: 'Complete Your Profile',
      description: 'Add a bio and photo so others can connect with you (~1 min)',
      completed: !!(profile?.bio && profile.bio.length > 10),
      route: '/me?openEditor=true',
    },
    {
      id: 'rsvp',
      title: 'RSVP to Your First Event',
      description: "Tap an event → hit 'RSVP' to save your spot. Check future dates if none this week!",
      completed: hasRSVP,
      route: '/events',
    },
    {
      id: 'buddy',
      title: 'Try the Buddy System',
      description: "Tip: Toggle 'Find a Buddy' when RSVPing to get matched with someone",
      completed: hasUsedBuddy,
      route: '/events',
    },
    {
      id: 'herd',
      title: 'Join a Herd',
      description: "Browse communities → tap 'Join' on one that interests you",
      completed: hasJoinedHerd,
      route: '/herds',
    },
    {
      id: 'friend',
      title: 'Connect with Someone',
      description: hasRSVP 
        ? "Visit someone's profile → tap 'Add Friend' to connect"
        : "Tip: RSVP to an event first to meet people attending the same events",
      completed: hasConnectedFriend,
      route: '/social',
    },
    {
      id: 'attend',
      title: 'Attend Your First Event',
      description: 'Show up and tap "Check In" when you arrive at the venue',
      completed: hasAttended,
      route: '/schedule',
    },
  ];

  // Use demo checklist for guests, real checklist for authenticated users
  const checklist = isGuest ? demoChecklist : userChecklist;
  const completedCount = checklist.filter(item => item.completed).length;
  const totalCount = checklist.length;
  const progressPercent = (completedCount / totalCount) * 100;
  const isComplete = completedCount === totalCount;

  // Award achievements when checklist items are completed - OPTIMIZED: batch check
  useEffect(() => {
    const awardAchievements = async () => {
      if (!user || isGuest) return;

      const achievementMap: Record<string, string> = {
        profile: 'onboarding_profile',
        rsvp: 'onboarding_rsvp',
        buddy: 'onboarding_buddy',
        herd: 'onboarding_herd',
        friend: 'onboarding_friend',
      };

      // Get all completed items that have achievement mappings
      const completedItems = checklist.filter(
        item => item.completed && achievementMap[item.id]
      );

      // Early exit if nothing to award
      if (completedItems.length === 0 && !isComplete) return;

      // BATCH: Fetch all existing onboarding achievements in one query
      const { data: existingAchievements } = await supabase
        .from('user_achievements')
        .select('achievement_type')
        .eq('user_id', user.id);

      const existingTypes = new Set(existingAchievements?.map(a => a.achievement_type) || []);

      // Collect all missing achievements to insert
      const achievementsToInsert: Array<{
        user_id: string;
        achievement_type: any;
        metadata: Record<string, unknown>;
      }> = [];

      for (const item of completedItems) {
        const achievementType = achievementMap[item.id];
        if (!existingTypes.has(achievementType as any)) {
          achievementsToInsert.push({
            user_id: user.id,
            achievement_type: achievementType as any,
            metadata: { checklist_item: item.id }
          });
        }
      }

      // Add completion achievement if all done
      if (isComplete && !existingTypes.has('onboarding_complete')) {
        achievementsToInsert.push({
          user_id: user.id,
          achievement_type: 'onboarding_complete',
          metadata: { completed_at: new Date().toISOString() }
        });
      }

      // BATCH INSERT: Insert all missing achievements in one query
      if (achievementsToInsert.length > 0) {
        await supabase
          .from('user_achievements')
          .insert(achievementsToInsert as any);
      }
    };

    awardAchievements();
  }, [checklist, isComplete, user, isGuest]);

  // Trigger celebration when checklist is completed
  useEffect(() => {
    if (isComplete && hasCompletionAchievement === false && !hasShownCelebration) {
      setShowCelebration(true);
      setHasShownCelebration(true);
      
      triggerHaptic();
      
      const duration = 3000;
      const end = Date.now() + duration;
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F9CA24'];

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: colors,
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: colors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();

      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: colors,
        });
        triggerHaptic();
      }, 200);

      if (user?.id) {
        localStorage.setItem(`onboarding_celebration_shown_${user.id}`, 'true');
      }
    }
  }, [isComplete, hasCompletionAchievement, hasShownCelebration]);

  // For guests, show the demo checklist (don't hide)
  // For authenticated users, apply normal visibility logic
  if (!isGuest) {
    // Don't render during auth state transitions - prevents flash during token refresh
    // If user was previously authenticated but is now null, wait for auth to stabilize
    if (!isAuthStable || (!user && wasAuthenticated.current)) {
      return null;
    }

    // Don't render until we have confirmed data. This also covers the failure
    // case: never show a misleading "0 of 6" built from errored queries.
    if (!onboardingStatus) {
      return null;
    }


    // Hide if user already has the onboarding_complete achievement (or local cache says so)
    if ((hasCompletionAchievement || localCompletionStatus) && !showCelebration) {
      return null;
    }
  }

  return (
    <>
      <Dialog open={showCelebration} onOpenChange={setShowCelebration}>
        <DialogContent className="sm:max-w-md border-none bg-gradient-to-br from-background via-background to-primary/5">
          <div className="flex flex-col items-center text-center space-y-6 py-6">
            <div className="relative">
              <div 
                className="absolute inset-0 rounded-full blur-2xl opacity-50 animate-pulse"
                style={{ backgroundColor: 'hsl(48 100% 50%)' }}
              />
              <div 
                className="relative w-24 h-24 rounded-full flex items-center justify-center animate-bounce"
                style={{ 
                  background: 'linear-gradient(135deg, hsl(48 100% 50%), hsl(45 100% 60%))',
                  boxShadow: '0 0 40px hsl(48 100% 50% / 0.5)'
                }}
              >
                <Trophy className="w-12 h-12 text-white" />
              </div>
            </div>

            <div className="space-y-2 animate-fade-in">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                You're All Set! 🎉
              </h2>
              <p className="text-muted-foreground text-base max-w-sm">
                Congratulations! You've completed all the getting started steps. 
                You're ready to make the most of Wellness Cowgirl!
              </p>
            </div>

            <p className="text-sm text-muted-foreground italic animate-fade-in">
              "Your wellness journey starts now. Let's ride!"
            </p>

            <Button 
              onClick={() => setShowCelebration(false)}
              className="mt-4"
            >
              Let's Go!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card data-tour-id="onboarding-checklist" className="p-6 card-shadow bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-bold text-foreground">Getting Started</h3>
            <p className="text-sm text-muted-foreground">
              {completedCount} of {totalCount} completed
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? "Collapse getting started checklist" : "Expand getting started checklist"}
          aria-expanded={isExpanded}
          className="h-11 w-11 p-0"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </div>

      <Progress value={progressPercent} className="h-2 mb-4" />

      {isExpanded && (
        <div className="space-y-3">
          {checklist.map((item) => (
            <div
              key={item.id}
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg transition-all tap-scale",
                item.completed 
                  ? "bg-background/50 opacity-75" 
                  : "bg-background cursor-pointer hover:bg-accent/50",
                !item.completed && "animate-in fade-in duration-200"
              )}
              onClick={() => {
                console.log('[OnboardingChecklist] Item clicked:', item.id, 'completed:', item.completed);
                
                if (!item.completed) {
                  console.log('[OnboardingChecklist] Processing incomplete item');
                  
                  // For guests, prompt sign up instead of navigating
                  if (isGuest) {
                    console.log('[OnboardingChecklist] Guest mode - prompting signup');
                    promptSignUp("Create an account to start your wellness journey and complete these steps!");
                    return;
                  }
                  
                  // NAVIGATE FIRST - before any async operations (critical for iOS)
                  const targetRoute = item.id === 'profile' ? '/me?openEditor=true' : item.route;
                  console.log('[OnboardingChecklist] Target route:', targetRoute);
                  
                  if (targetRoute) {
                    navigate(targetRoute);
                    console.log('[OnboardingChecklist] Navigation called successfully');
                  } else {
                    console.warn('[OnboardingChecklist] No target route for item:', item.id);
                  }
                  
                  // Trigger haptic AFTER navigation is queued (non-blocking, isolated)
                  try {
                    import('despia-native')
                      .then(({ default: despia }) => despia('haptic://'))
                      .catch(() => {}); // Silently ignore errors
                  } catch (e) {
                    // Ignore any synchronous errors from iOS
                  }
                }
              }}
            >
              {item.completed ? (
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-medium text-sm",
                  item.completed ? "text-muted-foreground line-through" : "text-foreground"
                )}>
                  {item.title}
                </p>
                {!item.completed && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isExpanded && (
        <p className="text-xs text-muted-foreground text-center mt-4">
          Complete these steps to unlock the full Wellness Cowgirl experience!
        </p>
      )}
      </Card>
    </>
  );
};
